import React, { useEffect, useState } from "react";
import { NavLink, useNavigate, Outlet, useLocation } from "react-router-dom";
import {
  LayoutDashboard,
  Users,
  UserPlus,
  Calendar,
  Package,
  FileText,
  BarChart3,
  LogOut,
  Menu,
  Settings,
  HelpCircle,
  UserCog,
  Store, // Vendor icon
} from "lucide-react";

import "../styles/layout.css";
import companyLogo from "../assets/ZAUNTRIP-Logo.png";

const navigationItems = [
  { title: "Dashboard",      path: "/dashboard", icon: LayoutDashboard },
  { title: "Leads",          path: "/leads",     icon: UserPlus },
  { title: "Client Details", path: "/clients",   icon: Users },
  { title: "Agent Details",  path: "/agents",    icon: UserCog },
  { title: "Vendor Details", path: "/vendors",   icon: Store },   // ← NEW
  { title: "Confirm Bookings", path: "/bookings",   icon: Calendar },
  { title: "Final Itinerary",  path: "/itineraries", icon: FileText },
  // { title: "Packages", path: "/packages", icon: Package },
  { title: "Reports",        path: "/analytics", icon: BarChart3 },
];

const secondaryItems = [
  // { title: "Settings", path: "/settings", icon: Settings },
  // { title: "Help & Support", path: "/help", icon: HelpCircle },
];

export default function Layout() {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [user, setUser]               = useState(null);
  const [isMobile, setIsMobile]       = useState(window.innerWidth < 768);
  const [activePage, setActivePage]   = useState("Dashboard");
  const navigate  = useNavigate();
  const location  = useLocation();

  useEffect(() => {
    const storedUser = localStorage.getItem("authUser");
    if (storedUser) {
      try { setUser(JSON.parse(storedUser)); }
      catch (error) {
        console.error("Error parsing user data:", error);
        localStorage.removeItem("authUser");
        localStorage.removeItem("authToken");
      }
    }

    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
      if (window.innerWidth >= 768) setSidebarOpen(true);
      else setSidebarOpen(false);
    };

    window.addEventListener("resize", handleResize);
    handleResize();
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const path = location.pathname;
    const currentPage = navigationItems.find(
      (item) => path === item.path || path.startsWith(item.path + "/")
    );
    if (currentPage) setActivePage(currentPage.title);
  }, [location]);

  const handleLogout = () => {
    try {
      localStorage.removeItem("authToken");
      localStorage.removeItem("authUser");
      localStorage.removeItem("userPreferences");
      navigate("/signin");
    } catch (error) {
      console.error("Logout error:", error);
      window.location.href = "/signin";
    }
  };

  const toggleSidebar = () => setSidebarOpen(!sidebarOpen);

  const getBreadcrumb = () => {
    const path = location.pathname;
    if (path === "/" || path === "/dashboard") return "Home / Dashboard";
    const item = navigationItems.find(
      (item) => path === item.path || path.startsWith(item.path + "/")
    );
    if (item) {
      if (path.includes("/leads/") && !path.endsWith("/leads")) return "Home / Leads / Details";
      return `Home / ${item.title}`;
    }
    return "Home";
  };

  return (
    <div className="layout-container">
      {isMobile && sidebarOpen && (
        <div className="sidebar-overlay" onClick={() => setSidebarOpen(false)} />
      )}

      <aside className={`sidebar ${sidebarOpen ? "open" : "closed"}`}>
        <div className="sidebar-header">
          <div className="logo-container">
            <img src={companyLogo} alt="Company Logo" className="company-logo" />
          </div>
        </div>

        <div className="sidebar-content">
          <nav className="sidebar-nav">
            <p className="nav-label">MAIN NAVIGATION</p>

            <div className="nav-items-group">
              {navigationItems.map((item) => (
                <NavLink
                  key={item.title}
                  to={item.path}
                  className={({ isActive }) => `nav-item ${isActive ? "active" : ""}`}
                  onClick={() => {
                    if (isMobile) setSidebarOpen(false);
                    setActivePage(item.title);
                  }}
                  end={item.path === "/dashboard"}
                >
                  <div className="nav-icon"><item.icon size={18} /></div>
                  <span className="nav-title">{item.title}</span>
                </NavLink>
              ))}
            </div>

            <p className="nav-label secondary-label">PREFERENCES</p>

            <div className="nav-items-group">
              {secondaryItems.map((item) => (
                <NavLink
                  key={item.title}
                  to={item.path}
                  className={({ isActive }) => `nav-item ${isActive ? "active" : ""}`}
                  onClick={() => isMobile && setSidebarOpen(false)}
                >
                  <div className="nav-icon"><item.icon size={18} /></div>
                  <span className="nav-title">{item.title}</span>
                </NavLink>
              ))}
            </div>
          </nav>

          <div className="sidebar-footer">
            <div className="user-profile">
              <div className="user-avatar">
                {user?.full_name?.[0]?.toUpperCase() || "U"}
              </div>
              <div className="user-info">
                <p className="user-name">{user?.full_name || "User Name"}</p>
                <p className="user-role">{user?.role || "Travel Agent"}</p>
              </div>
              <button className="logout-btn" onClick={handleLogout} title="Logout">
                <LogOut size={16} />
              </button>
            </div>
          </div>
        </div>
      </aside>

      <div className="main-content">
        <header className="main-header">
          <div className="header-left">
            <button
              className="menu-toggle-btn"
              onClick={toggleSidebar}
              aria-label={sidebarOpen ? "Close sidebar" : "Open sidebar"}
            >
              <Menu size={20} />
            </button>
            <div className="header-title">
              <h1>{activePage}</h1>
              <p className="breadcrumb">{getBreadcrumb()}</p>
            </div>
          </div>

          <div className="header-right">
            <div className="header-actions">
              <button className="notification-btn" aria-label="Notifications">
                <div className="notification-icon">
                  <span className="notification-badge">3</span>
                  🔔
                </div>
              </button>
              <div className="user-dropdown">
                <div className="user-avatar-small">
                  {user?.full_name?.[0]?.toUpperCase() || "U"}
                </div>
                <div className="user-dropdown-info">
                  <span className="user-name">{user?.full_name || "User"}</span>
                  <span className="user-role">{user?.role || "Agent"}</span>
                </div>
              </div>
            </div>
          </div>
        </header>

        <main className="page-content">
          <Outlet />
        </main>
      </div>
    </div>
  );
}